import React, { useState } from 'react';
import { X, CheckCircle, XCircle, Clock, AlertCircle, Timer, Info, Eye, EyeOff, UserCircle, Save } from 'lucide-react';
// Eye/EyeOff kept for existing password display
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Badge from '../../ui/Badge';
import { ManualGameDeposit } from '../../../types';
import { useUpdateManualGameDepositAccount } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import { formatCurrency, cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';

interface ManualGameDepositDetailProps {
  deposit: ManualGameDeposit;
  onClose: () => void;
  onConfirm: () => void;
  onReject: () => void;
}

const ManualGameDepositDetail: React.FC<ManualGameDepositDetailProps> = ({
  deposit,
  onClose,
  onConfirm,
  onReject,
}) => {
  const fmt = useFormatDate();
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [gameUsername, setGameUsername] = useState('');
  const [gamePassword, setGamePassword] = useState('');
  const [showExistingPassword, setShowExistingPassword] = useState(false);

  const updateAccountMutation = useUpdateManualGameDepositAccount();

  const handleUpdateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameUsername.trim()) return;

    try {
      await updateAccountMutation.mutateAsync({
        transactionId: deposit.transactionId,
        data: {
          gameUsername: gameUsername.trim(),
          ...(gamePassword.trim() ? { gamePassword: gamePassword.trim() } : {}),
        },
      });
      setShowAccountForm(false);
      setGamePassword('');
    } catch {
      // error handled by hook
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string; icon: typeof Clock }> = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      completed: { label: 'Confirmed', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      expired: { label: 'Expired', className: 'bg-gray-100 text-gray-600', icon: AlertCircle },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800', icon: XCircle },
    };

    const statusConfig = config[status] || config.pending;
    const Icon = statusConfig.icon;

    return (
      <span className={cn('px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5', statusConfig.className)}>
        <Icon className="h-4 w-4" />
        {statusConfig.label}
      </span>
    );
  };

  const gameAccount = deposit.gameAccount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Manual Deposit Detail</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status & Time Remaining */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Status:</span>
              {getStatusBadge(deposit.status)}
            </div>
            {deposit.status === 'pending' && deposit.timeRemaining && (
              <div className="flex items-center gap-1.5 text-orange-600">
                <Timer className="h-4 w-4" />
                <span className="text-sm font-medium">Time Remaining: {deposit.timeRemaining}</span>
              </div>
            )}
          </div>

          {/* Transaction Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <DetailRow label="Transaction ID" value={deposit.transactionId} mono />
            {deposit.userPhone && (
              <div className="flex items-start justify-between gap-4">
                <span className="text-sm text-muted-foreground whitespace-nowrap">User Phone</span>
                <Link
                  to={`/users/${deposit.userId}`}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  {deposit.userPhone}
                </Link>
              </div>
            )}
            {deposit.username && <DetailRow label="Username" value={deposit.username} />}
            <div className="border-t pt-3 mt-3" />
            <DetailRow label="Game" value={deposit.gameProvider.name} highlight />
            <DetailRow
              label="Amount"
              value={`${formatCurrency(deposit.amount)} ${deposit.currency || 'USD'}`}
              highlight
            />
            <div className="border-t pt-3 mt-3" />
            <DetailRow
              label="Created"
              value={fmt(deposit.createdAt)}
            />
            <DetailRow
              label="Expires"
              value={fmt(deposit.expiresAt)}
            />
            {deposit.confirmedAt && (
              <DetailRow
                label="Confirmed At"
                value={fmt(deposit.confirmedAt)}
              />
            )}
            {deposit.confirmedBy && (
              <DetailRow label="Confirmed By" value={deposit.confirmedBy} mono />
            )}
            {deposit.rejectedAt && (
              <DetailRow
                label="Rejected At"
                value={fmt(deposit.rejectedAt)}
              />
            )}
            {deposit.rejectedBy && (
              <DetailRow label="Rejected By" value={deposit.rejectedBy} mono />
            )}
            {deposit.rejectionReason && (
              <DetailRow label="Rejection Reason" value={deposit.rejectionReason} />
            )}
            {deposit.adminNotes && (
              <DetailRow label="Admin Notes" value={deposit.adminNotes} />
            )}
          </div>

          {/* Game Account Section */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <UserCircle className="h-4 w-4" />
                Game Account
              </h4>
              {!showAccountForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setGameUsername('');
                    setGamePassword('');
                    setShowAccountForm(true);
                  }}
                >
                  {gameAccount ? 'Edit Account' : 'Create Account'}
                </Button>
              )}
            </div>

            {gameAccount ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Username</span>
                  <span className="font-medium">{gameAccount.gameUsername}</span>
                </div>
                {gameAccount.password && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Password</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">
                        {showExistingPassword ? gameAccount.password : '••••••••'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowExistingPassword(!showExistingPassword)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {showExistingPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={gameAccount.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {gameAccount.status}
                  </Badge>
                </div>
                {gameAccount.gameAccountId && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account ID</span>
                    <span className="font-mono text-xs">{gameAccount.gameAccountId}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{fmt(gameAccount.createdAt)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No game account linked to this user for {deposit.gameProvider.name}.
              </p>
            )}

            {/* Inline Edit/Create Form */}
            {showAccountForm && (
              <form onSubmit={handleUpdateAccount} className="space-y-3 border-t pt-3" autoComplete="off">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Game Username <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="text"
                    name="game-user-field"
                    placeholder="e.g., player123"
                    value={gameUsername}
                    onChange={(e) => setGameUsername(e.target.value)}
                    maxLength={100}
                    required
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Game Password
                  </label>
                  <Input
                    type="text"
                    name="game-pwd-field"
                    placeholder={gameAccount?.password ? '(unchanged if empty)' : 'e.g., secret456'}
                    value={gamePassword}
                    onChange={(e) => setGamePassword(e.target.value)}
                    maxLength={100}
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAccountForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!gameUsername.trim() || updateAccountMutation.isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {updateAccountMutation.isLoading ? (
                      <div className="flex items-center">
                        <LoadingSpinner />
                        <span className="ml-2">Saving...</span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                        {gameAccount ? 'Update Account' : 'Create Account'}
                      </div>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Workflow Instructions - only show for pending */}
          {deposit.status === 'pending' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">Workflow Instructions</h4>
                  <ol className="text-sm text-blue-700 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>{deposit.gameProvider.name}</strong> game platform</li>
                    <li>Deposit <strong>{formatCurrency(deposit.amount)}</strong> for user <strong>{deposit.userEmail}</strong></li>
                    <li>After successful deposit, click <strong>&quot;Confirm Deposit&quot;</strong> below</li>
                    <li>If unable to deposit, click <strong>&quot;Reject&quot;</strong> and provide a reason</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {deposit.status === 'pending' && (
            <div className="flex items-center justify-between pt-2 border-t">
              <Button
                variant="outline"
                onClick={onReject}
                className="text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={onConfirm}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Deposit
              </Button>
            </div>
          )}

          {/* Close button for non-pending */}
          {deposit.status !== 'pending' && (
            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for detail rows
const DetailRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}> = ({ label, value, mono, highlight }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-sm text-muted-foreground whitespace-nowrap">{label}</span>
    <span
      className={cn(
        'text-sm text-right break-all',
        mono && 'font-mono text-xs',
        highlight && 'font-semibold text-foreground'
      )}
    >
      {value}
    </span>
  </div>
);

export default ManualGameDepositDetail;
